(function() {
  'use strict';

  const domCache = new Map();
  const eventListeners = new Set();
  
  function getCachedElement(selector) {
    if (!domCache.has(selector)) {
      const element = document.querySelector(selector);
      if (element) domCache.set(selector, element);
      return element;
    }
    return domCache.get(selector);
  }

  function createIntersectionObserver(callback, options = {}) {
    if (!('IntersectionObserver' in window)) return null;
    
    return new IntersectionObserver(callback, {
      rootMargin: '50px',
      threshold: 0.1,
      ...options
    });
  }

  function initializeRecharge() {
    const STOREFRONT_ACCESS_TOKEN = window.STOREFRONT_ACCESS_TOKEN;
    if (typeof recharge === 'undefined' || !STOREFRONT_ACCESS_TOKEN) return;
    
    try {
      recharge.init({
        storeIdentifier: window.SHOP_PERMANENT_DOMAIN,
        storefrontAccessToken: STOREFRONT_ACCESS_TOKEN,
        loginRetryFn: () => recharge.auth.loginShopifyAppProxy(),
      });
    } catch (error) {
      console.warn('Recharge initialization failed:', error);
    }
  }

  function initializeSmoothScroll() {
    const smoothScrollHandler = (e) => {
      const anchor = e.target.closest('a[href^="#"]');
      if (!anchor) return;
      
      e.preventDefault();
      const targetId = anchor.getAttribute('href');
      const target = getCachedElement(targetId);
      
      if (target) {
        target.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }
    };

    document.addEventListener('click', smoothScrollHandler, { passive: false });
    eventListeners.add(() => document.removeEventListener('click', smoothScrollHandler));
  }

  function initializeRotatingText() {
    const container = getCachedElement('#rotating-text-container');
    if (!container) return;

    const items = container.querySelectorAll('.text-item');
    const itemCount = items.length;
    
    if (itemCount <= 1) return;

    let currentIndex = 0;
    let animationId;
    let lastTime = 0;
    const ROTATION_INTERVAL = 2000; // 2 seconds
    
    items[currentIndex]?.classList.add('active');

    function rotateText(currentTime) {
      if (currentTime - lastTime >= ROTATION_INTERVAL) {
        items[currentIndex]?.classList.remove('active');
        currentIndex = (currentIndex + 1) % itemCount;
        items[currentIndex]?.classList.add('active');
        lastTime = currentTime;
      }
      
      if (!document.hidden) {
        animationId = requestAnimationFrame(rotateText);
      }
    }

    // Start animation
    animationId = requestAnimationFrame(rotateText);

    const handleVisibilityChange = () => {
      if (document.hidden) {
        cancelAnimationFrame(animationId);
      } else {
        lastTime = performance.now();
        animationId = requestAnimationFrame(rotateText);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    const cleanup = () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    
    eventListeners.add(cleanup);
    window.addEventListener('beforeunload', cleanup, { once: true });
  }

  function initializeJudgeMe() {
    try {
      const badgePlaceholder = getCachedElement('#judgeme_badge_placeholder');
      const widgetPlaceholder = getCachedElement('#judgeme_widget_placeholder');
      
      if (!badgePlaceholder && !widgetPlaceholder) return;

      const fragment = document.createDocumentFragment();
      
      if (badgePlaceholder) {
        const previewBadge = getCachedElement('.jdgm-preview-badge');
        if (previewBadge) {
          const badgeClone = previewBadge.cloneNode(true);
          fragment.appendChild(badgeClone);
          badgePlaceholder.appendChild(fragment);
        }
      }
      
      if (widgetPlaceholder) {
        const reviewWidget = getCachedElement('#judgeme_product_reviews');
        if (reviewWidget) {
          widgetPlaceholder.style.visibility = 'visible';
          widgetPlaceholder.style.height = 'auto';
          
          const observer = createIntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                const widgetClone = reviewWidget.cloneNode(true);
                entry.target.appendChild(widgetClone);
                observer.unobserve(entry.target);
              }
            });
          });
          
          if (observer) {
            observer.observe(widgetPlaceholder);
          } else {
            widgetPlaceholder.appendChild(reviewWidget.cloneNode(true));
          }
        }
      }
    } catch (error) {
      if (window.console?.warn) {
        console.warn('Judge.me initialization failed:', error);
      }
    }
  }

  function initializeLazyFeatures() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        initializeRotatingText();
        initializeJudgeMe();
      });
    } else {
      setTimeout(() => {
        initializeRotatingText();
        initializeJudgeMe();
      }, 100);
    }
  }

  function initialize() {
    initializeSmoothScroll();
    initializeRecharge();
    
    initializeLazyFeatures();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})();
